/**
 * The two record drills this chapter mounts beside the consolidation door
 * (`door-drill.ts`, ADR 7002): one exit plan, `/exit/plans/:exitPlanId`, and one cadence
 * proposal, `/exit/cadence/:proposalId`.
 *
 * Each is mounted because a published read serves exactly that record, and on that record's
 * own key:
 *
 *   `GET /exit/plans/{exit_plan_id}`                 one plan, by its identifier
 *   `GET /exit/cadence-proposals?proposal_id=…`      the queue narrowed to one proposal
 *
 * The plan drill is keyed on `exit_plan_id`, never on the option: an option can carry more
 * than one plan across seasons, so an option-keyed link would resolve silently to whichever
 * row sorted first (`scope.ts`, `EXIT_PLAN_ID_PARAM`). It REPLACES the `?exitPlanId=` query
 * link the plan book used to write, and that link keeps working: `legacyPlanRedirect` names
 * the path it now lives at.
 *
 * Pure, so the screens, their exports and the row links share one spelling and the tests
 * assert on strings.
 */

import { EXIT_PLAN_ID_PARAM } from './scope';

export const PLANS_PATH = '/exit/plans';
/** The manifest's declared drill target, verbatim. */
export const PLAN_DRILL_PATTERN = '/exit/plans/:exitPlanId';

export const CADENCE_PATH = '/exit/cadence';
/** The manifest's declared drill target, verbatim. */
export const PROPOSAL_DRILL_PATTERN = '/exit/cadence/:proposalId';

/** The one segment a pathname names under `base`, or `null`. A blank or undecodable segment is none. */
function segmentUnder(base: string, pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const prefix = base.split('/').filter(Boolean);
  if (!prefix.every((segment, index) => segments[index] === segment)) return null;
  if (segments.length !== prefix.length + 1) return null;
  try {
    const value = decodeURIComponent(segments[prefix.length] ?? '').trim();
    return value === '' ? null : value;
  } catch {
    return null;
  }
}

/** The exit plan a pathname names, or `null`. */
export function readPlanDrill(pathname: string): string | null {
  return segmentUnder(PLANS_PATH, pathname);
}

/** The cadence proposal a pathname names, or `null`. */
export function readProposalDrill(pathname: string): string | null {
  return segmentUnder(CADENCE_PATH, pathname);
}

export function planPath(exitPlanId: string): string {
  return `${PLANS_PATH}/${encodeURIComponent(exitPlanId)}`;
}

export function proposalPath(proposalId: string): string {
  return `${CADENCE_PATH}/${encodeURIComponent(proposalId)}`;
}

/**
 * Where a `/exit/plans?exitPlanId=…` link now lives, or `null` when there is nothing to move.
 *
 * Every other query key is carried across, so a link that also held a filter keeps it; a
 * blank `?exitPlanId=` is no plan (the rule `readExitScope` applies) and redirects nowhere.
 */
export function legacyPlanRedirect(pathname: string, search: string): string | null {
  if (pathname.replace(/\/+$/, '') !== PLANS_PATH) return null;
  const params = new URLSearchParams(search);
  const id = params.get(EXIT_PLAN_ID_PARAM)?.trim() ?? '';
  if (id === '') return null;
  params.delete(EXIT_PLAN_ID_PARAM);
  const rest = params.toString();
  return rest === '' ? planPath(id) : `${planPath(id)}?${rest}`;
}
